import { constants, translateDataLayerEvent } from "@qgiv/core-js";

const { DataLayerEvent } = constants.ENUMS;

/**
 * @private
 * @function createCustomQgivAnalyticsEvent
 * @param {object} data Payload containing event data to be dispatched.
 * @description Function that creates and dispatches a custom event on the document
 *              using the event name specified in the `data.event` property. The
 *              event includes the provided data as its `detail` property.
 */
const createCustomQgivAnalyticsEvent = (data) => {
    if (data.event) {
        const customEvent = new CustomEvent(data.event, { detail: data });
        document.dispatchEvent(customEvent);
    }
};

/**
 * @function createDataLayerEvent
 * @param {object} data Payload that is being sent to the parent webpage.
 * @description Function that emits a dataLayer event that is received by
 *              a handlers in embed.development.js and used to update the data
 *              layer of the parent webpage if one is present.
 */
const createDataLayerEvent = (data = {}) => {
    // Industry standard way to create a data layer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);

    createCustomQgivAnalyticsEvent(data);
};

/**
 * @private
 * @function createPurchaseEvent
 * @param {object} data Payload containing event data for the purchase.
 * @description Function that handles different types of purchase events and
 *              triggers the appropriate tracking mechanism (Google Analytics 4 or Meta Pixel).
 *              Logs a warning if the event type is unknown.
 */
const createPurchaseEvent = (data) => {
    if (data.event === "QGIV.ga4Purchase" && window.gtag) {
        window.gtag("event", "purchase", data.ecommerce);
    }
    if (data.event === "QGIV.metaPixelPurchase" && window.fbq) {
        window.fbq("track", "Purchase", data.ecommerce);
    }
};

/**
 * @function getQgivEventNames
 * @description Generates a list of valid QGIV event names by translating DataLayerEvent enums
 *              and filtering out any invalid or empty names.
 * @returns {string[]} An array of valid QGIV event names.
 */
const getQgivEventNames = () => {
    const dataLayerEvents = Object.values(DataLayerEvent);
    const translatedEventNames = dataLayerEvents.map((event) =>
        translateDataLayerEvent(event),
    );
    const validEventNames = translatedEventNames.filter(Boolean);

    return validEventNames;
};

// Set event names, for use within 'on'
const qgivEventNames = getQgivEventNames();

/**
 * @function on
 * @param {string} eventName The name of the event to listen for.
 * @param {Function} callback The function to execute when the event is triggered.
 * @description Function that adds an event listener to the document for the specified event name
 *              and executes the provided callback function when the event is triggered.
 *              Note that this implementation pulls its qgivEventNames strings from translateDataLayerEvent,
 *              to avoid the need to hardcode them inside the function, as was necessary for the embed.js implementation.
 *              A list of such events is maintained at:
 *              https://www.notion.so/bloomerang/Analytics-API-dd90dee31d6f4e96bbe67a4f5f371bcf?pvs=4
 */
const on = (eventName, callback) => {
    if (!qgivEventNames.includes(eventName)) {
        throw new Error(
            `Invalid event name: ${eventName}. Please see QGIV's developer documentation for a list of accepted event names.`,
        );
    }

    if (typeof callback !== "function") {
        throw new Error("Invalid argument: callback must be a function.");
    }

    document.addEventListener(eventName, callback);
};

/**
 * @function loadCustomAnalyticsEventHandling
 * @description Function that initializes the QGIV object on the global window object
 *              and assigns the `on` method to it for handling custom analytics events.
 */
const loadCustomAnalyticsEventHandling = () => {
    const QGIV = window.QGIV || {};

    QGIV.on = on;
};

export {
    createDataLayerEvent,
    createPurchaseEvent,
    loadCustomAnalyticsEventHandling,
};
